import Footer from "@components/ui/Footer"
import OptimizedImage from "@components/ui/OptimizedImage"
import { useRoutePageContext } from "@components/ui/route-page-context"
import { imageRenderProps, imageUrl } from "@lib/component-utils"
import { sanitizeRichHtml } from "@lib/sanitize-html"
import * as m from "@paraglide/messages"
import type { ContentImage, HomePageSettings } from "@site-types/content"
import type { CSSProperties } from "react"

type BrandsProps = {
  brandImage?: ContentImage
  settings: HomePageSettings
}

function backgroundVar(source?: string) {
  return source ? `url("${source}")` : undefined
}

export function Brands({ brandImage, settings }: BrandsProps) {
  const { locale } = useRoutePageContext()
  const backgroundSources = {
    taiyoro: imageUrl(settings.homePageTaiyoroBackground as ContentImage | undefined, "workCover"),
    playdata: imageUrl(settings.homePagePlaydataBackground as ContentImage | undefined, "workCover"),
    flux: imageUrl(settings.homePageFluxBackground as ContentImage | undefined, "workCover"),
  }
  const brands = [
    {
      id: "futaroku",
      backgroundSrc: undefined,
      href: m.home_brands_futaroku_href({}, { locale: locale }),
      imgSrc: settings.homePageFutarokuLogo as ContentImage | undefined,
      imgAlt: m.home_brands_futaroku({}, { locale: locale }),
      tagLine: m.home_brands_futaroku_tagLine({}, { locale: locale }).toUpperCase(),
      blurb: m.home_brands_futaroku_blurb({}, { locale: locale }),
    },
    {
      id: "taiyoro",
      backgroundSrc: backgroundSources.taiyoro,
      href: m.home_brands_taiyoro_href({}, { locale: locale }),
      imgSrc: settings.homePageTaiyoroLogo as ContentImage | undefined,
      imgAlt: m.home_brands_taiyoro({}, { locale: locale }),
      tagLine: m.home_brands_taiyoro_tagLine({}, { locale: locale }).toUpperCase(),
      blurb: m.home_brands_taiyoro_blurb({}, { locale: locale }),
    },
    {
      id: "playdata",
      backgroundSrc: backgroundSources.playdata,
      href: m.home_brands_playdata_href({}, { locale: locale }),
      imgSrc: settings.homePagePlaydataLogo as ContentImage | undefined,
      imgAlt: m.home_brands_playdata({}, { locale: locale }),
      tagLine: m.home_brands_playdata_tagLine({}, { locale: locale }).toUpperCase(),
      blurb: m.home_brands_playdata_blurb({}, { locale: locale }),
    },
    {
      id: "flux",
      backgroundSrc: backgroundSources.flux,
      href: m.home_brands_flux_href({}, { locale: locale }),
      imgSrc: settings.homePageFluxLogo as ContentImage | undefined,
      imgAlt: m.home_brands_flux({}, { locale: locale }),
      tagLine: m.home_brands_flux_tagLine({}, { locale: locale }).toUpperCase(),
      blurb: m.home_brands_flux_blurb({}, { locale: locale }),
    },
  ]

  return (
    <section
      className="relative flex min-h-[100dvh] w-full flex-col overflow-x-hidden [scroll-snap-align:start]"
      id="brands"
      data-testid="home-brands"
    >
      <div
        className="w-full md:m-auto md:grid md:grid-cols-4 md:grid-rows-2 md:py-[30px]"
        data-testid="home-brands-grid"
      >
        <div
          className="group m-[1em] grid place-content-center px-[1em] py-[2em] text-center text-[1.4em] text-white no-underline underline-offset-0 [container-type:inline-size] [grid-row:1_/_span_2] [transition-duration:500ms,500ms] [transition-property:scale,color] [transition-timing-function:ease,ease] hover:no-underline md:w-full md:[grid-column:span_4] md:[grid-row:1] xl:[grid-column:span_2] xl:[grid-row:span_2]"
          data-brand="playbrain"
        >
          <span className="w-full place-self-center">
            {brandImage ? (
              <OptimizedImage
                className="relative mx-auto block h-[20lvh] w-4/5"
                alt="PlayBrain"
                {...imageRenderProps(brandImage, "logo")}
              />
            ) : null}
          </span>
          <span className="[font-size:max(1.6em,2vw)]">
            <span
              className="hidden md:my-[1em] md:block md:text-[0.8em] md:[text-wrap:balance]"
              data-testid="home-brands-playbrain-blurb"
              dangerouslySetInnerHTML={{
                __html: sanitizeRichHtml(m.home_brands_description({}, { locale: locale })),
              }}
            />
            <a
              href={`/${locale}/work`}
              className="mx-auto mt-[1em] inline-block w-fit border border-[var(--white-50)] px-[1em] text-[1em] text-[var(--white-50)] leading-[1.6em] no-underline [transition-duration:300ms,300ms] [transition-property:border-color,color] [transition-timing-function:ease,ease] hover:cursor-pointer hover:border-white hover:text-white hover:no-underline"
            >
              {m.home_brands_button({}, { locale: locale })}
            </a>
          </span>
        </div>
        {brands.map((brand) => (
          <a
            key={brand.id}
            className={`group m-[1em] grid px-[1em] py-[2em] text-center text-[1.4em] no-underline underline-offset-0 [container-type:inline-size] hover:no-underline ${
              brand.id === "flux"
                ? ""
                : "[transition-duration:500ms,500ms] [transition-property:scale,color] [transition-timing-function:ease,ease]"
            } [clip-path:polygon(1.5em_0%,calc(100%_-_1.5em)_0%,100%_1.5em,100%_calc(100%_-_1.5em),calc(100%_-_1.5em)_100%,1.5em_100%,0%_calc(100%_-_1.5em),0%_1.5em)] md:block ${
              brand.id === "futaroku"
                ? "bg-[#fdea14] text-[#111] hover:text-[#111]"
                : brand.id === "taiyoro"
                  ? "bg-[red] bg-contain text-white hover:text-white"
                  : brand.id === "playdata"
                    ? "bg-[#19191e] bg-cover text-white bg-blend-overlay hover:text-white"
                    : "bg-[teal] bg-contain text-white duration-[600ms] ease-[ease] [background-image:radial-gradient(circle_at_128.8423px_-32.8635px,#9f6aea_0%,#3288ce_51.63%,#1ac68d_100%)] [transition-property:background-image] hover:bg-[radial-gradient(circle_at_128.8423px_-32.8635px,#9f6aea_20%,#3288ce_60%,#1ac68d_100%)] hover:text-white"
            }`}
            href={brand.href}
            target="_blank"
            rel="noreferrer"
            data-brand={brand.id}
            data-remote-background-src={brand.backgroundSrc}
            style={
              brand.backgroundSrc && brand.id !== "flux"
                ? ({ backgroundImage: backgroundVar(brand.backgroundSrc) } as CSSProperties)
                : undefined
            }
          >
            <span className="w-full place-self-center md:flex md:h-[100px] md:items-center md:[place-self:baseline]">
              {brand.imgSrc ? (
                <OptimizedImage
                  className="mx-auto w-4/5 [transition-duration:300ms,300ms] [transition-property:scale,transform] [transition-timing-function:ease,ease] md:h-auto md:w-full group-hover:[scale:1.25] group-hover:[transform:translateY(-2px)]"
                  alt={brand.imgAlt}
                  {...imageRenderProps(brand.imgSrc, "logo")}
                />
              ) : null}
            </span>
            <span className="[font-size:max(1em,1vw)]">
              <strong className="my-[1em] block text-[1.2em] [transition-duration:300ms,300ms] [transition-property:scale,transform] [transition-timing-function:ease,ease] group-hover:[scale:1.05] group-hover:[transform:translateY(8px)]">
                {locale === "ja" ? brand.tagLine.toLocaleLowerCase() : brand.tagLine}
              </strong>
              <span
                className="hidden md:my-[1em] md:block md:text-[0.8em] md:[text-wrap:balance] md:[transition-duration:300ms,300ms] md:[transition-property:scale,transform] md:[transition-timing-function:ease,ease] md:group-hover:[scale:1.03] md:group-hover:[transform:translateY(15px)]"
                data-testid={`home-brand-blurb-${brand.id}`}
              >
                {brand.blurb}
              </span>
            </span>
          </a>
        ))}
      </div>
      <Footer />
    </section>
  )
}

export default Brands
